import React, { FormEvent, useEffect, useState } from 'react';

import * as JKYModel from '../custom-types';
import api from '../utils/Api';
import { trackEvent } from '../utils/Analytics';
import {
  ANSWER_LETTERS,
  DIFFICULTY_LABELS,
  TeamFaces,
  GlobalRank,
  TriviaAvatar,
  formatPoints,
  ordinal,
  rankBy,
  roundLabel,
  useCountdown,
} from './TriviaShared';

import './TriviaPlay.scss';

interface TriviaPlayProps {
  trivia: JKYModel.TriviaState;
  playerIdentifier: string;
  venueName: string;
  token: string;
  currentUser: JKYModel.User;
  onAddSongs(): void;
}

/**
 * A patron's phone during trivia night: join, then tap an answer while the question is on
 * the TV. The question is on the phone too, for anyone who can't see the screen from
 * their seat. Everything else (the leaderboard, the clock) is the server's, as the TV shows it.
 */
const TriviaPlay = ({ trivia, playerIdentifier, venueName, token, currentUser, onAddSongs }: TriviaPlayProps) => {
  const [picked, setPicked] = useState<{ questionNumber: number; index: number }>(null);
  const [joining, setJoining] = useState(false);
  const [joinedHere, setJoinedHere] = useState(false);
  const [message, setMessage] = useState<string>(null);

  const [changingTeam, setChangingTeam] = useState(false);

  const players = trivia.players || [];
  const me = players.find((p) => currentUser && p.id === currentUser.id);
  const teamsMode = trivia.mode === 'teams';
  const teams = trivia.teams || [];
  const myTeam = teamsMode && me && me.teamId !== null ? teams.find((t) => t.id === me.teamId) : undefined;
  const rolling = !!trivia.rolling;
  // In rolling trivia people drop off the board by leaving or by sitting a few out, so the
  // board is the only word on whether this phone is in.
  const joined = teamsMode ? !!myTeam : rolling ? !!me : !!me || joinedHere || !!(trivia.me && trivia.me.joined);
  // Teams are fixed once the first question has been asked, except in rolling trivia.
  const canChangeTeam = teamsMode && (rolling || (trivia.questionNumber || 0) === 0);
  const phase = trivia.phase;
  const round = (trivia.rounds || [])[trivia.roundIndex || 0];
  const kicker = 'Final scores';

  // A reload mid-question gets back the answer this phone already sent.
  useEffect(() => {
    if (trivia.me && trivia.me.answerIndex !== null && trivia.me.answerIndex !== undefined) {
      setPicked({ questionNumber: trivia.questionNumber, index: trivia.me.answerIndex });
    }
  }, [trivia.me && trivia.me.answerIndex, trivia.questionNumber]);

  useEffect(() => setMessage(null), [trivia.questionNumber, phase]);

  const myPick = picked && picked.questionNumber === trivia.questionNumber ? picked.index : null;

  const join = async (choice: { teamId?: number; teamName?: string } = {}) => {
    setJoining(true);
    setMessage(null);
    try {
      await api.trivia(`${playerIdentifier}/join`, 'POST', token, choice);
      trackEvent('trivia_joined', {
        player_code: playerIdentifier,
        team: choice.teamId ? 'existing' : choice.teamName ? 'new' : 'own',
      });
      setJoinedHere(true);
      setChangingTeam(false);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setJoining(false);
    }
  };

  const leave = async () => {
    setJoining(true);
    setMessage(null);
    try {
      await api.trivia(`${playerIdentifier}/leave`, 'POST', token, {});
      trackEvent('trivia_left', { player_code: playerIdentifier });
      setJoinedHere(false);
      setChangingTeam(false);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setJoining(false);
    }
  };

  const answer = async (index: number) => {
    if (myPick !== null) {
      return;
    }
    const questionNumber = trivia.questionNumber;
    // Shown as locked in at once; a refusal takes it back and says why.
    setPicked({ questionNumber, index });
    setMessage(null);
    try {
      await api.trivia(`${playerIdentifier}/answer`, 'POST', token, { questionNumber, answerIndex: index });
      trackEvent('trivia_answered', { player_code: playerIdentifier, question_number: questionNumber });
      setJoinedHere(true);
    } catch (error) {
      setPicked(null);
      setMessage(error.message);
    }
  };

  return (
    <div className="trivia-phone">
      <div className="trivia-phone-top">
        <div>
          <div className="trivia-phone-kicker">{rolling ? 'Rolling Trivia' : 'Trivia Night'}</div>
          <div className="trivia-phone-venue">{venueName}</div>
          {!!me && !!me.globalRank && (
            <div className="trivia-phone-global">
              Global rank <GlobalRank person={me} showWins />
            </div>
          )}
        </div>
        {!!myTeam && (
          <div className="trivia-phone-me">
            <div className="trivia-phone-team-pill">{myTeam.name}</div>
            <div>
              <div className="trivia-phone-score">{formatPoints(myTeam.score)}</div>
              <div className="trivia-phone-rank">
                {ordinal(myTeam.rank)} of {teams.length}
              </div>
            </div>
          </div>
        )}
        {!!me && !teamsMode && (
          <div className="trivia-phone-me">
            <TriviaAvatar person={me} className="trivia-phone-avatar" />
            <div>
              <div className="trivia-phone-score">{formatPoints(me.score)}</div>
              <div className="trivia-phone-rank">
                {ordinal(me.rank)} of {players.length}
              </div>
            </div>
          </div>
        )}
      </div>

      {teamsMode && (phase !== 'final' || rolling) && (!myTeam || (changingTeam && canChangeTeam)) && (
        <TeamPicker
          teams={teams}
          busy={joining}
          late={phase !== 'lobby'}
          onJoin={join}
          onCancel={myTeam ? () => setChangingTeam(false) : undefined}
        />
      )}

      {teamsMode && phase === 'lobby' && !!myTeam && !changingTeam && (
        <div className="trivia-phone-card">
          <h2>You're on {myTeam.name}</h2>
          <TeamFaces
            members={myTeam.members}
            max={8}
            className="trivia-phone-faces"
            avatarClassName="trivia-phone-avatar"
          />
          <p>Everyone answers on their own phone. Your team scores the average of its players.</p>
          <p className="trivia-phone-soft">Watch the TV. The first question is on its way.</p>
          <button type="button" className="trivia-phone-switch" onClick={() => setChangingTeam(true)}>
            Switch team
          </button>
        </div>
      )}

      {!teamsMode && phase === 'lobby' && (
        <div className="trivia-phone-card">
          {joined ? (
            <>
              <h2>You're in!</h2>
              <p>Watch the TV. The first question is on its way.</p>
              <p className="trivia-phone-soft">
                {players.length} {players.length === 1 ? 'player' : 'players'} so far.
              </p>
            </>
          ) : (
            <>
              <h2>Play along</h2>
              <p>
                Answer on your phone as each question shows on the TV. The faster you get it right, the more points you
                score.
              </p>
              <button type="button" className="trivia-phone-join" disabled={joining} onClick={() => join()}>
                {joining ? 'Joining…' : `Join as ${currentUser ? currentUser.username : 'me'}`}
              </button>
            </>
          )}
          <ol className="trivia-phone-rounds">
            {(trivia.rounds || []).map((r, index) => (
              <li key={index}>
                <b>Round {index + 1}</b> {r.random ? 'Random category' : roundLabel(r)}
              </li>
            ))}
          </ol>
        </div>
      )}

      {phase === 'loading' && (
        <div className="trivia-phone-card">
          <h2>Getting the questions…</h2>
        </div>
      )}

      {phase === 'roundIntro' && (!teamsMode || !!myTeam) && (
        <div className="trivia-phone-card trivia-phone-center">
          <div className="trivia-phone-kicker">
            {rolling ? `Round ${trivia.lap || 1}` : `Round ${(trivia.roundIndex || 0) + 1} of ${trivia.roundCount}`}
          </div>
          <h2 className="trivia-phone-big">{round ? round.category : ''}</h2>
          <p className="trivia-phone-soft">
            {round ? DIFFICULTY_LABELS[round.difficulty] : ''} · {trivia.questionCount} questions
          </p>
          <p>Get ready.</p>
          {!joined && !teamsMode && (
            <button type="button" className="trivia-phone-join" disabled={joining} onClick={() => join()}>
              Join the game
            </button>
          )}
        </div>
      )}

      {rolling && !teamsMode && !joined && (phase === 'question' || phase === 'reveal') && (
        <p className="trivia-phone-jumpin">Join anytime: tap an answer to jump in.</p>
      )}

      {(phase === 'question' || phase === 'reveal') && !!trivia.question && (!teamsMode || !!myTeam) && (
        <PhoneQuestion trivia={trivia} myPick={myPick} me={me} team={myTeam} onAnswer={answer} />
      )}

      {(phase === 'standings' || (phase === 'final' && rolling)) && (
        <PhoneStandings trivia={trivia} me={me} team={myTeam} />
      )}

      {phase === 'final' &&
        !rolling &&
        (teamsMode ? (
          <PhoneFinalTeams teams={teams} team={myTeam} kicker={kicker} />
        ) : (
          <PhoneFinal players={players} me={me} kicker={kicker} />
        ))}

      {phase === 'final' && rolling && !teamsMode && !joined && (
        <button type="button" className="trivia-phone-join" disabled={joining} onClick={() => join()}>
          {joining ? 'Joining…' : 'Join the next round'}
        </button>
      )}

      {rolling && joined && !changingTeam && (
        <div className="trivia-phone-rolling-actions">
          {teamsMode && (
            <button type="button" className="trivia-phone-switch" onClick={() => setChangingTeam(true)}>
              Switch team
            </button>
          )}
          <button type="button" className="trivia-phone-switch" disabled={joining} onClick={leave}>
            Leave the game
          </button>
        </div>
      )}

      {!!message && <div className="jukebox-notice jukebox-notice-bad">{message}</div>}

      <button type="button" className="trivia-phone-songs" onClick={onAddSongs}>
        Not playing? Add songs
      </button>
      {!!trivia.credit && <div className="trivia-phone-credit">{trivia.credit}</div>}
    </div>
  );
};

interface TeamPickerProps {
  teams: JKYModel.TriviaTeam[];
  busy: boolean;
  late: boolean;
  onJoin(choice: { teamId?: number; teamName?: string }): void;
  onCancel?(): void;
}

/** Join a team already playing, or start one by typing its name. */
const TeamPicker = ({ teams, busy, late, onJoin, onCancel }: TeamPickerProps) => {
  const [name, setName] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) onJoin({ teamName: name.trim() });
  };
  return (
    <div className="trivia-phone-card">
      <h2>{late ? 'Pick a team to play' : 'Pick your team'}</h2>
      {teams.length > 0 && (
        <ul className="trivia-phone-teams">
          {teams.map((team) => (
            <li key={team.id}>
              <div className="trivia-phone-team-text">
                <div className="trivia-phone-team-name">{team.name}</div>
                <TeamFaces members={team.members} max={5} avatarClassName="trivia-phone-avatar-small" />
              </div>
              <button
                type="button"
                className="trivia-phone-team-join"
                disabled={busy}
                onClick={() => onJoin({ teamId: team.id })}
              >
                Join
              </button>
            </li>
          ))}
        </ul>
      )}
      <form className="trivia-phone-newteam" onSubmit={submit}>
        <label htmlFor="trivia-team-name">{teams.length > 0 ? 'Or start a new team' : 'Start a team'}</label>
        <div className="trivia-phone-newteam-row">
          <input
            id="trivia-team-name"
            type="text"
            maxLength={24}
            value={name}
            placeholder="Team name"
            onChange={(e) => setName(e.target.value)}
          />
          <button type="submit" disabled={busy || !name.trim()}>
            Start
          </button>
        </div>
      </form>
      {!!onCancel && (
        <button type="button" className="trivia-phone-switch" onClick={onCancel}>
          Keep my team
        </button>
      )}
    </div>
  );
};

interface PhoneQuestionProps {
  trivia: JKYModel.TriviaState;
  myPick: number | null;
  me: JKYModel.TriviaPlayer | undefined;
  team?: JKYModel.TriviaTeam;
  onAnswer(index: number): void;
}

const PhoneQuestion = ({ trivia, myPick, me, team, onAnswer }: PhoneQuestionProps) => {
  const revealing = trivia.phase === 'reveal';
  const { seconds, share } = useCountdown(trivia, revealing ? trivia.revealSeconds || 10 : trivia.answerSeconds || 20);
  const question = trivia.question;
  const closed = revealing || seconds === 0;

  let verdict: React.ReactNode = null;
  if (revealing) {
    const rightAnswer = question.answers[trivia.correctIndex];
    if (myPick === trivia.correctIndex) {
      verdict = (
        <div className="trivia-phone-verdict is-right">
          Correct! <b>+{formatPoints(me ? me.lastPoints : 0)}</b>
        </div>
      );
    } else if (myPick === null) {
      verdict = (
        <div className="trivia-phone-verdict">
          Out of time. It was <b>{rightAnswer}</b>.
        </div>
      );
    } else {
      verdict = (
        <div className="trivia-phone-verdict is-wrong">
          Not quite. It was <b>{rightAnswer}</b>.
        </div>
      );
    }
  }

  const teamLine =
    revealing && team ? (
      <div className="trivia-phone-teamline">
        {team.name}: {team.lastRightCount} of {team.members.length} right · +{formatPoints(team.lastPoints)}
      </div>
    ) : null;

  return (
    <div className="trivia-phone-question">
      <div className="trivia-phone-qtop">
        <span>
          Question {(trivia.questionIndex || 0) + 1} of {trivia.questionCount}
        </span>
        <span className="trivia-phone-seconds">{revealing ? `Next in ${seconds}` : `${seconds}s`}</span>
      </div>
      <div className="trivia-phone-bar">
        <div style={{ transform: `scaleX(${share})` }} />
      </div>
      <div className="trivia-phone-qtext">{question.text}</div>

      {verdict}
      {teamLine}
      {revealing && <PhoneGotItRight trivia={trivia} />}

      <div className={`trivia-phone-answers trivia-phone-answers-${question.answers.length}`}>
        {question.answers.map((text, index) => {
          const classes = ['trivia-phone-answer', `trivia-phone-answer-${index}`];
          if (myPick === index) classes.push('is-picked');
          if (myPick !== null && myPick !== index) classes.push('is-dim');
          if (revealing && trivia.correctIndex === index) classes.push('is-right');
          if (revealing && trivia.correctIndex !== index) classes.push('is-dim');
          return (
            <button
              type="button"
              key={index}
              className={classes.join(' ')}
              disabled={closed || myPick !== null}
              onClick={() => onAnswer(index)}
            >
              <span className="trivia-phone-letter">{ANSWER_LETTERS[index]}</span>
              <span className="trivia-phone-answer-text">{text}</span>
            </button>
          );
        })}
      </div>

      {!revealing && myPick !== null && <div className="trivia-phone-locked">Locked in. Watch the TV.</div>}
    </div>
  );
};

/** Once the answer shows, who got it right. */
const PhoneGotItRight = ({ trivia }: { trivia: JKYModel.TriviaState }) => {
  const names =
    trivia.mode === 'teams'
      ? (trivia.teams || []).filter((t) => t.lastRightCount > 0).map((t) => t.name)
      : (trivia.players || []).filter((p) => p.lastCorrect).map((p) => p.username);
  if (names.length === 0) {
    return <div className="trivia-phone-gotit">Nobody got that one.</div>;
  }
  const shown = names.slice(0, 6).join(', ');
  return (
    <div className="trivia-phone-gotit">
      Got it right: <b>{shown}</b>
      {names.length > 6 ? ` and ${names.length - 6} more` : ''}
    </div>
  );
};

interface PhoneStandingsProps {
  trivia: JKYModel.TriviaState;
  me: JKYModel.TriviaPlayer | undefined;
  team?: JKYModel.TriviaTeam;
}

/** Between rounds: the global rankings, the game so far, then the round, as on the TV. */
const PhoneStandings = ({ trivia, me, team }: PhoneStandingsProps) => {
  const rolling = !!trivia.rolling;
  const { seconds } = useCountdown(trivia, rolling ? 20 : 15);
  // A rolling game's rounds are its laps, and its player rankings are the day's points.
  const roundNumber = rolling ? trivia.lap || 1 : (trivia.roundIndex || 0) + 1;
  const teamsMode = trivia.mode === 'teams';
  const people: any[] = teamsMode ? trivia.teams || [] : trivia.players || [];
  const overall = rankBy(people, (x) => (rolling ? x.sessionScore || 0 : x.score));
  const ofRound = rankBy(people, (x) => (rolling ? x.score : x.roundScore || 0));
  const leaders = trivia.globalLeaders || [];
  const meId = me ? me.id : -1;
  const mine = teamsMode ? team : me;
  const placeOf = (ranked: { item: any; rank: number }[]) => {
    const found = mine && ranked.find((r) => r.item.id === mine.id);
    return found ? found.rank : 0;
  };

  const list = (ranked: { item: any; rank: number }[], score: (x: any) => number) => (
    <ol className="trivia-phone-podium">
      {ranked.slice(0, 3).map(({ item, rank }) => (
        <li key={item.id} className={mine && item.id === mine.id ? 'is-me' : ''}>
          <span className="trivia-phone-place">{ordinal(rank)}</span>
          <span className="trivia-phone-name">{teamsMode ? item.name : item.username}</span>
          <span className="trivia-phone-points">{formatPoints(score(item))}</span>
        </li>
      ))}
    </ol>
  );

  return (
    <div className="trivia-phone-card">
      <div className="trivia-phone-kicker">
        Round {roundNumber} done · next round in {seconds}
      </div>

      <h3 className="trivia-phone-standings-head">Global rankings</h3>
      {leaders.length === 0 ? (
        <p className="trivia-phone-soft">No wins yet. Win a round to get on the board.</p>
      ) : (
        <ol className="trivia-phone-podium">
          {leaders.slice(0, 3).map((l) => (
            <li key={l.id} className={l.id === meId ? 'is-me' : ''}>
              <span className="trivia-phone-place">#{l.globalRank}</span>
              <TriviaAvatar person={l} className="trivia-phone-avatar" />
              <span className="trivia-phone-name">{l.username}</span>
              <span className="trivia-phone-points">
                {l.wins} {l.wins === 1 ? 'win' : 'wins'}
              </span>
            </li>
          ))}
        </ol>
      )}
      {!!me && !leaders.slice(0, 3).some((l) => l.id === meId) && (
        <p className="trivia-phone-soft">
          {me.globalRank ? <>You are #{me.globalRank} worldwide.</> : 'Win a round to get a global rank.'}
        </p>
      )}

      <h3 className="trivia-phone-standings-head">{teamsMode ? 'Team rankings' : 'Player rankings'}</h3>
      {list(overall, (x) => (rolling ? x.sessionScore || 0 : x.score))}
      {!!placeOf(overall) && (
        <p className="trivia-phone-soft">
          You are {ordinal(placeOf(overall))} {rolling ? 'today' : 'in this game'}.
        </p>
      )}

      <h3 className="trivia-phone-standings-head">Round {roundNumber} rankings</h3>
      {list(ofRound, (x) => (rolling ? x.score : x.roundScore || 0))}
      {!!placeOf(ofRound) && <p className="trivia-phone-soft">You were {ordinal(placeOf(ofRound))} this round.</p>}
    </div>
  );
};

interface PhoneFinalProps {
  players: JKYModel.TriviaPlayer[];
  me: JKYModel.TriviaPlayer | undefined;
  kicker: string;
}

const PhoneFinal = ({ players, me, kicker }: PhoneFinalProps) => (
  <div className="trivia-phone-card trivia-phone-center">
    <div className="trivia-phone-kicker">{kicker}</div>
    {me ? (
      <h2 className="trivia-phone-big">{me.rank === 1 ? 'You won!' : `You came ${ordinal(me.rank)}`}</h2>
    ) : (
      <h2 className="trivia-phone-big">Thanks for watching</h2>
    )}
    {!!me && (
      <p>
        {formatPoints(me.score)} points · {me.correctCount} right
      </p>
    )}
    <ol className="trivia-phone-podium">
      {players.slice(0, 3).map((p) => (
        <li key={p.id}>
          <span className="trivia-phone-place">{ordinal(p.rank)}</span>
          <TriviaAvatar person={p} className="trivia-phone-avatar" />
          <span className="trivia-phone-name">
            {p.username} <GlobalRank person={p} />
          </span>
          <span className="trivia-phone-points">{formatPoints(p.score)}</span>
        </li>
      ))}
    </ol>
  </div>
);

interface PhoneFinalTeamsProps {
  teams: JKYModel.TriviaTeam[];
  team: JKYModel.TriviaTeam | undefined;
  kicker: string;
}

const PhoneFinalTeams = ({ teams, team, kicker }: PhoneFinalTeamsProps) => (
  <div className="trivia-phone-card trivia-phone-center">
    <div className="trivia-phone-kicker">{kicker}</div>
    {team ? (
      <h2 className="trivia-phone-big">
        {team.rank === 1 ? `${team.name} won!` : `${team.name} came ${ordinal(team.rank)}`}
      </h2>
    ) : (
      <h2 className="trivia-phone-big">Thanks for watching</h2>
    )}
    {!!team && <p>{formatPoints(team.score)} points</p>}
    <ol className="trivia-phone-podium">
      {teams.slice(0, 3).map((t) => (
        <li key={t.id}>
          <span className="trivia-phone-place">{ordinal(t.rank)}</span>
          <span className="trivia-phone-name">{t.name}</span>
          <TeamFaces members={t.members} max={3} avatarClassName="trivia-phone-avatar-small" />
          <span className="trivia-phone-points">{formatPoints(t.score)}</span>
        </li>
      ))}
    </ol>
  </div>
);

export default TriviaPlay;
